import {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  setAvailableFilters as setAvailableFiltersAction,
  addFilter as addFilterAction,
  removeAddedFilter as removeAddedFilterAction,
  toggleFilter as toggleFilterAction,
  setFilterValue as setFilterValueAction,
  addMultipleFilters as addMultipleFiltersAction,
  setMultipleFilterValues as setMultipleFilterValuesAction,
  clearAllFilterValues as clearAllFilterValuesAction,
  clearAllFilters as clearAllFiltersAction,
  removeAllAddedFilters as removeAllAddedFiltersAction,
  clearCategoryFilters as clearCategoryFiltersAction,
  setDisabledFilters as setDisabledFiltersAction,
  setSelectedState as setSelectedStateAction,
  resetDirtyFlag as resetDirtyFlagAction,
  importFilterState as importFilterStateAction,
  setPendingFilterValue as setPendingFilterValueAction,
  setPendingFilterValues as setPendingFilterValuesAction,
  applyPendingFilterValue as applyPendingFilterValueAction,
  clearPendingFilterValue as clearPendingFilterValueAction,
  clearAllPendingFilterValues as clearAllPendingFilterValuesAction,
  setFilterRangeOptions as setFilterRangeOptionsAction,
  addToFiltersRemoved as addToFiltersRemovedAction,
  addToFiltersReset as addToFiltersResetAction,
  clearFiltersRemovedAndReset as clearFiltersRemovedAndResetAction,
  selectAvailableFilters,
  selectAddedFilters,
  selectAppliedFilters,
  selectFilterMetadata,
  selectDisabledFilters,
  selectSelectedState,
  selectIsDirty,
  selectCategoryFilterCounts,
  selectTotalAddedFiltersCount,
  selectTotalAppliedFiltersCount,
  selectAddableFilters,
  selectPendingFilterValues,
  selectRemovedAndResetFilters
} from '../slices/filterManagementSlice'
import {selectFilterConfig} from '../slices/configSlice'
import {
  getFilterConfig,
  getDisabledFilters,
  convertFiltersToQueryParams,
  getFilterSummary,
  getOutreachQueryParams
} from '../../utils/filterManagementHelpers'

/**
 * Custom hook for filter management with three-level structure
 *
 * Level 1: availableFilters - Filters user can access (from API)
 * Level 2: addedFilters - Filters visible in drawer (user choice)
 * Level 3: appliedFilters - Filters with values set (affecting data)
 *
 * Provides a clean interface for managing filters throughout the application.
 */
export const useFilterManagement = () => {
  const dispatch = useDispatch()
  const availableFilters = useSelector(selectAvailableFilters)
  const addedFilters = useSelector(selectAddedFilters)
  const appliedFilters = useSelector(selectAppliedFilters)
  const filterMetadata = useSelector(selectFilterMetadata)
  const disabledFilters = useSelector(selectDisabledFilters)
  const selectedState = useSelector(selectSelectedState)
  const isDirty = useSelector(selectIsDirty)
  const categoryFilterCounts = useSelector(selectCategoryFilterCounts)
  const totalAddedFiltersCount = useSelector(selectTotalAddedFiltersCount)
  const totalAppliedFiltersCount = useSelector(selectTotalAppliedFiltersCount)
  const addableFilters = useSelector(selectAddableFilters)
  const filterConfig = useSelector(selectFilterConfig)
  const pendingFilterValues = useSelector(selectPendingFilterValues)
  const removedAndResetFilters = useSelector(selectRemovedAndResetFilters)

  const setAvailableFilters = useCallback(
    (filterKeys) => {
      dispatch(setAvailableFiltersAction(filterKeys))
    },
    [dispatch]
  )

  const addFilter = useCallback(
    (key) => {
      const config = getFilterConfig(key, filterConfig)
      if (!config) {
        console.warn(`Filter config not found for key: ${key}`)
        return
      }
      dispatch(
        addFilterAction({
          key,
          categoryId: config.categoryId,
          type: config.type
        })
      )
    },
    [dispatch, filterConfig]
  )

  const removeFilter = useCallback(
    (key) => {
      dispatch(removeAddedFilterAction(key))
    },
    [dispatch]
  )

  const toggleFilter = useCallback(
    (key) => {
      const config = getFilterConfig(key, filterConfig)
      if (!config) {
        console.warn(`Filter config not found for key: ${key}`)
        return
      }
      dispatch(
        toggleFilterAction({
          key,
          categoryId: config.categoryId,
          type: config.type
        })
      )
    },
    [dispatch, filterConfig]
  )

  const setFilterValue = useCallback(
    (key, value) => {
      dispatch(setFilterValueAction({key, value}))
    },
    [dispatch]
  )

  const addMultipleFilters = useCallback(
    (filterKeys) => {
      const filtersWithMetadata = filterKeys
        .map((key) => {
          const config = getFilterConfig(key, filterConfig)
          if (!config) {
            console.warn(`Filter config not found for key: ${key}`)
          }
          return {
            key,
            categoryId: config?.categoryId,
            type: config?.type
          }
        })
        .filter((f) => {
          if (!f.categoryId || !f.type) {
            console.warn(`Skipping filter ${f.key} - missing categoryId or type`)
            return false
          }
          return true
        })
      dispatch(addMultipleFiltersAction(filtersWithMetadata))
    },
    [dispatch, filterConfig]
  )

  const setMultipleFilterValues = useCallback(
    (filtersObj) => {
      dispatch(setMultipleFilterValuesAction(filtersObj))
    },
    [dispatch]
  )

  const clearAllFilterValues = useCallback(() => {
    dispatch(clearAllFilterValuesAction())
  }, [dispatch])

  const clearAllFilters = useCallback(() => {
    dispatch(clearAllFiltersAction())
  }, [dispatch])

  const removeAllAddedFilters = useCallback(() => {
    dispatch(removeAllAddedFiltersAction())
  }, [dispatch])

  const clearCategoryFilters = useCallback(
    (categoryId) => {
      dispatch(clearCategoryFiltersAction(categoryId))
    },
    [dispatch]
  )

  const getFilterValue = useCallback(
    (key) => {
      return appliedFilters[key]
    },
    [appliedFilters]
  )

  const isFilterAdded = useCallback(
    (key) => {
      return addedFilters.includes(key)
    },
    [addedFilters]
  )

  const isFilterApplied = useCallback(
    (key) => {
      return key in appliedFilters
    },
    [appliedFilters]
  )

  const isFilterAvailable = useCallback(
    (key) => {
      return availableFilters.includes(key)
    },
    [availableFilters]
  )

  const isFilterDisabled = useCallback(
    (key) => {
      return disabledFilters.includes(key)
    },
    [disabledFilters]
  )

  const canAddFilter = useCallback(
    (key) => {
      return availableFilters.includes(key) && !disabledFilters.includes(key) && !addedFilters.includes(key)
    },
    [availableFilters, disabledFilters, addedFilters]
  )

  const getCategoryFilters = useCallback(
    (categoryId) => {
      const filters = {}
      for (const [key, metadata] of Object.entries(filterMetadata)) {
        if (metadata.categoryId === categoryId && addedFilters.includes(key)) {
          filters[key] = appliedFilters[key]
        }
      }
      return filters
    },
    [appliedFilters, filterMetadata, addedFilters]
  )

  const getCategoryActiveCount = useCallback(
    (categoryId) => {
      return categoryFilterCounts[categoryId] || 0
    },
    [categoryFilterCounts]
  )

  const setSelectedState = useCallback(
    (state) => {
      dispatch(setSelectedStateAction(state))
      const disabled = getDisabledFilters(state, filterConfig)
      dispatch(setDisabledFiltersAction(disabled))
    },
    [dispatch, filterConfig]
  )

  const importFilterState = useCallback(
    (state) => {
      dispatch(importFilterStateAction(state))
    },
    [dispatch]
  )

  const exportFilterState = useCallback(() => {
    return {
      addedFilters,
      appliedFilters,
      filterMetadata,
      timestamp: Date.now(),
      version: '1.0'
    }
  }, [addedFilters, appliedFilters, filterMetadata])

  const getQueryParams = useCallback(() => {
    return convertFiltersToQueryParams(appliedFilters, filterMetadata)
  }, [appliedFilters, filterMetadata])

  const getOutreachParams = useCallback(() => {
    return getOutreachQueryParams(appliedFilters, filterMetadata, filterConfig)
  }, [appliedFilters, filterMetadata, filterConfig])

  const getFilterSummaryText = useCallback(() => {
    return getFilterSummary(appliedFilters, filterConfig)
  }, [appliedFilters, filterConfig])

  const resetDirtyFlag = useCallback(() => {
    dispatch(resetDirtyFlagAction())
  }, [dispatch])

  const setPendingFilterValue = useCallback(
    (key, value) => {
      dispatch(setPendingFilterValueAction({key, value}))
    },
    [dispatch]
  )

  const setPendingFilterValues = useCallback(
    (filtersObj) => {
      dispatch(setPendingFilterValuesAction(filtersObj))
    },
    [dispatch]
  )

  const applyPendingFilterValue = useCallback(
    (key) => {
      dispatch(applyPendingFilterValueAction(key))
    },
    [dispatch]
  )

  const clearPendingFilterValue = useCallback(
    (key) => {
      dispatch(clearPendingFilterValueAction(key))
    },
    [dispatch]
  )

  const clearAllPendingFilterValues = useCallback(() => {
    dispatch(clearAllPendingFilterValuesAction())
  }, [dispatch])

  const getPendingFilterValue = useCallback(
    (key) => {
      return pendingFilterValues[key]
    },
    [pendingFilterValues]
  )

  const hasPendingFilterValue = useCallback(
    (key) => {
      return key in pendingFilterValues
    },
    [pendingFilterValues]
  )

  // ========== Removed and Reset Filters (for notification banners) ==========

  /**
   * Add a filter label to the removed list (for notification banner)
   * @param {string} filterLabel - The display label of the filter
   */
  const addToFiltersRemoved = useCallback(
    (filterLabel) => {
      dispatch(addToFiltersRemovedAction(filterLabel))
    },
    [dispatch]
  )

  const addToFiltersReset = useCallback(
    (filterLabel) => {
      dispatch(addToFiltersResetAction(filterLabel))
    },
    [dispatch]
  )

  const clearFiltersRemovedAndReset = useCallback(() => {
    dispatch(clearFiltersRemovedAndResetAction())
  }, [dispatch])

  /**
   * Store fetched range options for a filter
   * @param {string} key - Filter key
   * @param {Object} rangeOptions - { min, max, minLabel, maxLabel }
   */
  const setFilterRangeOptions = useCallback(
    (key, rangeOptions) => {
      dispatch(setFilterRangeOptionsAction({key, rangeOptions}))
    },
    [dispatch]
  )

  /**
   * Get stored range options for a filter
   * @param {string} key - Filter key
   * @returns {Object|undefined} Range options or undefined if not set
   */
  const getFilterRangeOptions = useCallback(
    (key) => {
      return filterMetadata[key]?.rangeOptions
    },
    [filterMetadata]
  )

  return {
    availableFilters,
    addedFilters,
    appliedFilters,
    filterMetadata,
    disabledFilters,
    selectedState,
    isDirty,
    categoryFilterCounts,
    totalAddedFiltersCount,
    totalAppliedFiltersCount,
    addableFilters,
    filterConfig,
    setAvailableFilters,
    addFilter,
    removeFilter,
    toggleFilter,
    addMultipleFilters,
    removeAllAddedFilters,
    setFilterValue,
    setMultipleFilterValues,
    clearAllFilterValues,
    clearAllFilters,
    clearCategoryFilters,
    setSelectedState,
    importFilterState,
    exportFilterState,
    resetDirtyFlag,
    getFilterValue,
    isFilterAdded,
    isFilterApplied,
    isFilterAvailable,
    isFilterDisabled,
    canAddFilter,
    getCategoryFilters,
    getCategoryActiveCount,
    getQueryParams,
    getOutreachParams,
    getFilterSummaryText,
    pendingFilterValues,
    setPendingFilterValue,
    setPendingFilterValues,
    applyPendingFilterValue,
    clearPendingFilterValue,
    clearAllPendingFilterValues,
    getPendingFilterValue,
    hasPendingFilterValue,
    removedAndResetFilters,
    addToFiltersRemoved,
    addToFiltersReset,
    clearFiltersRemovedAndReset,
    setFilterRangeOptions,
    getFilterRangeOptions
  }
}

export default useFilterManagement
